import React, { useState, useEffect } from 'react'
import {
  Settings, School, Calendar, Users, Shield, Database,
  Eye, Download, Save, Plus, Edit2, Trash2, X, ChevronRight,
  Trophy, TrendingUp, Award, Star, Crown, Bell, History,
  FileText, AlertCircle, Check, RefreshCw, Search, Filter,
  DollarSign, Package, Clock, UserPlus, LogOut, Activity,
  BarChart, PieChart, Zap, Sparkles, MapPin, Menu, Book, ExternalLink,
  ChevronDown, Monitor
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO } from 'date-fns'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate, useSearchParams } from 'react-router-dom'

// Import sub-components we'll create
import { SystemSettings } from '@/components/admin/SystemSettings'
import { SchoolCalendarManager } from '@/components/admin/SchoolCalendarManager'
import { UserManagement } from '@/components/admin/UserManagement'
import { AuditLogs } from '@/components/admin/AuditLogs'
import { LeaderboardManager } from '@/components/admin/LeaderboardManager'
import { KidEventsManager } from '@/components/admin/KidEventsManager'
import { CalendarExport } from '@/components/admin/CalendarExport'
import { DeductionEditor } from '@/components/admin/DeductionEditor'
import { NotificationManager } from '@/components/admin/NotificationManager'
import { RewardsManager } from '@/components/admin/RewardsManager'
import { RotationGroupsManager } from '@/components/admin/RotationGroupsManager'
import { BackupRestore } from '@/components/admin/BackupRestore'
import { ApiDocumentation } from '@/components/admin/ApiDocumentation'
import { LocationsManagement } from '@/components/admin/LocationsManagement'
import ServiceManagement from '@/components/admin/ServiceManagement'
import NotificationSettings from '@/components/admin/NotificationSettings'
import { ParentChoreManager } from '@/components/ParentChoreManager'
import SaintsCalendar from '@/components/admin/SaintsCalendar'
import { CustodyManagement } from '@/components/admin/CustodyManagement'
import { SystemHealthComponent } from '@/components/admin/SystemHealthComponent'
import { ActiveSessions } from '@/components/admin/ActiveSessions'
import { CalendarSettings } from '@/components/admin/CalendarSettings'
import { SchoolCalendarImport } from '@/components/admin/SchoolCalendarImport'
import { HolidaySchedule } from '@/components/admin/HolidaySchedule'
import { UsageStatistics } from '@/components/admin/UsageStatistics'
import { PerformanceReports } from '@/components/admin/PerformanceReports'
import { ExportData } from '@/components/admin/ExportData'
import { IntegrationSettings } from '@/components/admin/IntegrationSettings'
import { DemoRequestsManager } from '@/components/admin/platform/DemoRequestsManager'
import { SignupRequestsManager } from '@/components/admin/platform/SignupRequestsManager'
import { PlatformFamiliesManager } from '@/components/admin/platform/PlatformFamiliesManager'
import { CalendarUploadManager } from '@/components/admin/platform/CalendarUploadManager'
import { KioskDeviceManager } from '@/components/admin/KioskDeviceManager'

export const AdminDashboard: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [activeSection, setActiveSection] = useState<string>(() => {
    // Check URL parameter for initial section
    return searchParams.get('section') || 'overview'
  })
  const [stats, setStats] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['Overview & Monitoring']) // Start with first group expanded
  const [searchQuery, setSearchQuery] = useState('')
  const [touchStartX, setTouchStartX] = useState<number | null>(null)
  const [version, setVersion] = useState<string>('')
  const { user } = useAuth()
  const navigate = useNavigate()

  // Check if user is parent/admin
  useEffect(() => {
    if (!user?.is_parent) {
      toast.error('Admin access required')
      navigate('/dashboard')
    }
  }, [user])

  // Auto-expand group containing active section
  useEffect(() => {
    const activeGroup = menuGroups.find(group => 
      group.items.some(item => item.id === activeSection)
    )
    if (activeGroup && !expandedGroups.includes(activeGroup.title)) {
      setExpandedGroups(prev => [...prev, activeGroup.title])
    }
  }, [activeSection])

  // Handle swipe gestures for mobile menu
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartX) return
    
    const touchEndX = e.touches[0].clientX
    const diff = touchStartX - touchEndX
    
    // Swipe left to close menu (only when menu is open)
    if (mobileMenuOpen && diff > 50) {
      setMobileMenuOpen(false)
      setTouchStartX(null)
    }
    // Swipe right to open menu (only from left edge)
    else if (!mobileMenuOpen && touchStartX < 50 && diff < -50) {
      setMobileMenuOpen(true)
      setTouchStartX(null)
    }
  }

  const handleTouchEnd = () => {
    setTouchStartX(null)
  }

  useEffect(() => {
    loadDashboardStats()
    fetchVersion()
  }, [])

  const fetchVersion = async () => {
    try {
      const response = await apiClient.get('/api/version')
      if (response?.version) {
        setVersion(response.version)
      }
    } catch (error) {
      console.error('Failed to fetch version:', error)
    }
  }

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const loadDashboardStats = async () => {
    setLoading(true)
    try {
      // Load various stats for overview
      const [users, chores, rewards, assignments] = await Promise.all([
        apiClient.get('/api/users'),
        apiClient.get('/api/chores'),
        apiClient.get('/api/rewards'),
        apiClient.get('/api/assignments')
      ])

      // Ensure all data is in array format (defensive check for API format differences)
      const usersArray = Array.isArray(users) ? users : []
      const choresArray = Array.isArray(chores) ? chores : []
      const rewardsArray = Array.isArray(rewards) ? rewards : []
      const assignmentsArray = Array.isArray(assignments) ? assignments : []

      setStats({
        totalUsers: usersArray.length,
        totalKids: usersArray.filter((u: any) => !u.is_parent).length,
        totalChores: choresArray.length,
        activeChores: choresArray.filter((c: any) => c.is_active).length,
        totalRewards: rewardsArray.length,
        activeRewards: rewardsArray.filter((r: any) => r.active).length,
        pendingAssignments: assignmentsArray.filter((a: any) => a.status === 'assigned').length,
        completedToday: assignmentsArray.filter((a: any) =>
          a.status === 'completed' &&
          new Date(a.completed_at).toDateString() === new Date().toDateString()
        ).length
      })
    } catch (error) {
      console.error('Failed to load dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  // Reorganized menu items according to DASHBOARD_IMPROVEMENTS.md
  const menuGroups = [
    {
      title: 'Overview & Monitoring',
      icon: BarChart,
      items: [
        { id: 'overview', label: 'Dashboard Overview', icon: BarChart, color: 'text-blue-500' },
        { id: 'system', label: 'System Health', icon: Activity, color: 'text-green-600' },
        { id: 'services', label: 'Background Services', icon: Zap, color: 'text-yellow-600' },
        { id: 'audit', label: 'Activity', icon: Eye, color: 'text-teal-500' }
      ]
    },
    {
      title: 'User & Access Management',
      icon: Users,
      items: [
        { id: 'users', label: 'User Management', icon: Users, color: 'text-green-500' },
        { id: 'sessions', label: 'Active Sessions', icon: Clock, color: 'text-orange-500' }
      ]
    },
    {
      title: 'Organization Setup',
      icon: School,
      items: [
        { id: 'locations', label: 'Locations', icon: MapPin, color: 'text-red-500' },
        { id: 'schools', label: 'Schools & Districts', icon: School, color: 'text-purple-500' },
        { id: 'calendar-settings', label: 'Calendar Settings', icon: Calendar, color: 'text-blue-600' }
      ]
    },
    {
      title: 'Chore & Reward System',
      icon: Trophy,
      items: [
        { id: 'chore-library', label: 'Chore Library', icon: Book, color: 'text-blue-500' },
        { id: 'rotations', label: 'Rotation Groups', icon: RefreshCw, color: 'text-indigo-500' },
        { id: 'leaderboard', label: 'Leaderboard Settings', icon: Trophy, color: 'text-yellow-500' },
        { id: 'rewards', label: 'Rewards Configuration', icon: Package, color: 'text-pink-500' },
        { id: 'deductions', label: 'Deduction Rules', icon: DollarSign, color: 'text-red-500' }
      ]
    },
    {
      title: 'Events & Scheduling',
      icon: Calendar,
      items: [
        { id: 'events', label: 'Kid Events Manager', icon: Calendar, color: 'text-orange-500' },
        { id: 'school-calendar', label: 'School Calendar Import', icon: Download, color: 'text-blue-500' },
        { id: 'holidays', label: 'Holiday Schedule', icon: Star, color: 'text-yellow-400' },
        { id: 'custody', label: 'Custody Calendar', icon: Users, color: 'text-purple-400' },
        { id: 'saints', label: 'Saints Calendar', icon: Calendar, color: 'text-purple-500' }
      ]
    },
    {
      title: 'Communications',
      icon: Bell,
      items: [
        { id: 'notifications', label: 'Notification Templates', icon: Bell, color: 'text-cyan-500' },
        { id: 'notification-settings', label: 'Email Settings', icon: Bell, color: 'text-blue-500' }
      ]
    },
    {
      title: 'Reports & Analytics',
      icon: PieChart,
      items: [
        { id: 'usage-stats', label: 'Usage Statistics', icon: TrendingUp, color: 'text-green-500' },
        { id: 'performance', label: 'Performance Reports', icon: BarChart, color: 'text-blue-500' },
        { id: 'export', label: 'Export Data', icon: Download, color: 'text-gray-500' }
      ]
    },
    {
      title: 'System Administration',
      icon: Settings,
      items: [
        { id: 'kiosk-devices', label: 'Kiosk Devices', icon: Monitor, color: 'text-blue-600' },
        { id: 'backup', label: 'Backup & Restore', icon: Database, color: 'text-gray-700' },
        { id: 'integrations', label: 'Integration Settings', icon: Zap, color: 'text-purple-600' },
        { id: 'api-docs', label: 'API Documentation', icon: Book, color: 'text-blue-600' },
        { id: 'system-settings', label: 'System Settings', icon: Settings, color: 'text-gray-600' }
      ]
    },
    // Only show Platform Management to platform admins
    ...(user?.is_platform_admin ? [{
      title: 'Platform Management',
      icon: Database,
      items: [
        { id: 'signup-requests', label: 'Signup Requests', icon: UserPlus, color: 'text-orange-600' },
        { id: 'demo-requests', label: 'Demo Access Requests', icon: Shield, color: 'text-yellow-600' },
        { id: 'platform-families', label: 'Platform Families', icon: Users, color: 'text-indigo-600' },
        { id: 'calendar-upload', label: 'Calendar Upload Tools', icon: Upload, color: 'text-purple-600' }
      ]
    }] : [])
  ]

  // Flatten for easy lookup
  const menuItems = menuGroups.flatMap(group => group.items)

  const renderContent = () => {
    try {
      switch (activeSection) {
        case 'overview':
          return <DashboardOverview stats={stats} onRefresh={loadDashboardStats} onNavigate={setActiveSection} />
        case 'users':
          return <UserManagement />
        case 'locations':
          return <LocationsManagement />
        case 'schools':
          return <SchoolCalendarManager />
        case 'events':
          return <KidEventsManager />
        case 'leaderboard':
          return <LeaderboardManager />
        case 'deductions':
          return <DeductionEditor />
        case 'chore-library':
          // The real chore manager (create, edit, schedules, rotations). The
          // old EnhancedChoreLibrary sent fields the API never stored.
          return <ParentChoreManager />
        case 'rewards':
          return <RewardsManager />
        case 'rotations':
          return <RotationGroupsManager />
        case 'notifications':
          return <NotificationManager />
        case 'notification-settings':
          return <NotificationSettings />
        case 'services':
          return <ServiceManagement />
        case 'calendar':
          return <CalendarExport />
        case 'audit':
          return <AuditLogs />
        case 'system':
          return <SystemHealthComponent />
        case 'sessions':
          return <ActiveSessions />
        case 'calendar-settings':
          return <CalendarSettings />
        case 'school-calendar':
          return <SchoolCalendarImport />
        case 'holidays':
          return <HolidaySchedule />
        case 'usage-stats':
          return <UsageStatistics />
        case 'performance':
          return <PerformanceReports />
        case 'export':
          return <ExportData />
        case 'integrations':
          return <IntegrationSettings />
        case 'system-health':
          return <SystemHealthComponent />
        case 'system-settings':
          return <SystemSettings />
        case 'backup':
          return <BackupRestore />
        case 'api-docs':
          return <ApiDocumentation />
        case 'saints':
          return <SaintsCalendar />
        case 'custody':
          return <CustodyManagement />
        case 'kiosk-devices':
          return <KioskDeviceManager />
        case 'signup-requests':
          return <SignupRequestsManager />
        case 'demo-requests':
          return <DemoRequestsManager />
        case 'platform-families':
          return <PlatformFamiliesManager />
        case 'calendar-upload':
          return <CalendarUploadManager />
        default:
          return <DashboardOverview stats={stats} onRefresh={loadDashboardStats} onNavigate={setActiveSection} />
      }
    } catch (error) {
      console.error(`Error rendering ${activeSection}:`, error)
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Component Error</h3>
          <p className="text-red-600">Failed to load {activeSection} section.</p>
          <p className="text-sm text-red-500 mt-2">{error?.message || 'Unknown error'}</p>
          <button
            onClick={() => setActiveSection('overview')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Return to Overview
          </button>
        </div>
      )
    }
  }

  return (
    <div 
      className="min-h-screen bg-gray-50"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-sm border-b px-4 py-3 sticky top-0 z-30">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex flex-col">
            <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
            {version && (
              <span className="text-xs text-gray-500">v{version}</span>
            )}
          </div>
          
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
            {user?.display_name?.[0] || 'A'}
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">System Management & Configuration {version && `• ${version}`}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Back to Dashboard
              </button>
              <button
                onClick={loadDashboardStats}
                className="p-2 text-gray-600 hover:text-gray-900"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Sidebar - Mobile Responsive */}
        <div className={`
          fixed lg:relative
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          w-64 bg-white shadow-md transition-transform duration-300 flex-shrink-0
          h-full z-50 lg:z-auto
          top-0 left-0 bottom-0
        `}>
          <div className="p-4 h-full overflow-y-auto">
            {/* Mobile Close Button */}
            <div className="flex items-center justify-between mb-4 lg:hidden">
              <span className="font-bold text-lg">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Mobile User Info & Actions */}
            <div className="lg:hidden mb-6 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {user?.display_name?.[0] || 'A'}
                  </div>
                  <span className="font-medium text-gray-900">{user?.display_name}</span>
                </div>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full flex items-center space-x-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                >
                  <Activity className="w-4 h-4" />
                  <span>Main Dashboard</span>
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center space-x-2 px-3 py-2 text-sm bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
            
            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search menu..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Grouped Menu with Collapsible Sections */}
            <div className="space-y-2">
              {menuGroups.map(group => {
                // Filter items based on search
                const filteredItems = searchQuery 
                  ? group.items.filter(item => 
                      item.label.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                  : group.items
                
                // Skip group if no items match search
                if (searchQuery && filteredItems.length === 0) return null
                
                const isExpanded = expandedGroups.includes(group.title) || searchQuery
                const hasActiveItem = group.items.some(item => item.id === activeSection)
                
                return (
                  <div key={group.title} className="space-y-1">
                    {/* Group Header */}
                    <button
                      onClick={() => {
                        setExpandedGroups(prev => 
                          isExpanded 
                            ? prev.filter(g => g !== group.title)
                            : [...prev, group.title]
                        )
                      }}
                      className={`w-full flex items-center justify-between px-3 py-3 lg:py-2 text-xs font-semibold uppercase tracking-wider rounded-lg transition-colors touch-manipulation ${
                        hasActiveItem 
                          ? 'bg-blue-50 text-blue-700' 
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <group.icon className="w-4 h-4" />
                        <span>{group.title}</span>
                      </div>
                      <ChevronDown 
                        className={`w-4 h-4 transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`} 
                      />
                    </button>
                    
                    {/* Group Items - Collapsible */}
                    {isExpanded && (
                      <ul className="ml-6 space-y-0.5">
                        {filteredItems.map(item => (
                          <li key={item.id}>
                            <button
                              onClick={() => {
                                setActiveSection(item.id)
                                setMobileMenuOpen(false)
                              }}
                              className={`w-full flex items-center space-x-3 px-3 py-3 lg:py-2 rounded-lg transition-colors touch-manipulation ${
                                activeSection === item.id
                                  ? 'bg-blue-100 text-blue-700 font-medium'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <item.icon className={`w-4 h-4 ${item.color}`} />
                              <span className="text-sm">{item.label}</span>
                              {activeSection === item.id && (
                                <ChevronRight className="w-3 h-3 ml-auto" />
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              renderContent()
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Dashboard Overview Component
const DashboardOverview: React.FC<{ stats: any; onRefresh: () => void; onNavigate: (section: string) => void }> = ({ stats, onRefresh, onNavigate }) => {
  const [jobsStatus, setJobsStatus] = React.useState<any>({ status: 'HEALTHY' })

  React.useEffect(() => {
    // Fetch background jobs status
    apiClient.get('/api/background-jobs/status')
      .then(data => setJobsStatus(data))
      .catch(err => console.error('Failed to fetch jobs status:', err))
  }, [])

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">System Overview</h2>
        <p className="text-gray-600">Quick stats and system health</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Users"
          value={stats.totalUsers || 0}
          subtitle={`${stats.totalKids || 0} kids`}
          icon={Users}
          color="bg-blue-500"
          onClick={() => onNavigate('users')}
        />
        <StatCard
          title="Active Chores"
          value={stats.activeChores || 0}
          subtitle={`of ${stats.totalChores || 0} total`}
          icon={Activity}
          color="bg-green-500"
          onClick={() => onNavigate('chore-library')}
        />
        <StatCard
          title="Active Rewards"
          value={stats.activeRewards || 0}
          subtitle={`of ${stats.totalRewards || 0} total`}
          icon={Trophy}
          color="bg-purple-500"
          onClick={() => onNavigate('rewards')}
        />
        <StatCard
          title="Completed Today"
          value={stats.completedToday || 0}
          subtitle={`${stats.pendingAssignments || 0} pending`}
          icon={Check}
          color="bg-orange-500"
          onClick={() => onNavigate('audit')}
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickActionButton
            label="Add User"
            icon={UserPlus}
            onClick={() => onNavigate('users')}
          />
          <QuickActionButton
            label="Export Data"
            icon={Download}
            onClick={() => onNavigate('calendar')}
          />
          <QuickActionButton
            label="View Logs"
            icon={FileText}
            onClick={() => onNavigate('audit')}
          />
          <QuickActionButton
            label="System Backup"
            icon={Database}
            onClick={() => onNavigate('backup')}
          />
        </div>
      </div>

      {/* System Health */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">System Health</h3>
        <div className="space-y-3">
          <HealthIndicator label="Database" status="healthy" />
          <HealthIndicator label="API Service" status="healthy" />
          <HealthIndicator 
            label={`Background Jobs${jobsStatus.pending_jobs > 0 ? ` (${jobsStatus.pending_jobs} pending)` : ''}`}
            status={jobsStatus.status === 'WARNING' ? 'warning' : jobsStatus.status === 'PENDING' ? 'warning' : 'healthy'} 
          />
          <HealthIndicator label="Storage" status="healthy" />
        </div>
      </div>
    </div>
  )
}

// Helper Components
const StatCard: React.FC<{
  title: string
  value: number
  subtitle: string
  icon: any
  color: string
  onClick?: () => void
}> = ({ title, value, subtitle, icon: Icon, color, onClick }) => (
  <div 
    className={`bg-white rounded-lg shadow p-6 ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-center justify-between mb-4">
      <div className={`p-3 rounded-lg ${color} bg-opacity-10`}>
        <Icon className={`w-6 h-6 ${color.replace('bg-', 'text-')}`} />
      </div>
      {onClick && (
        <ChevronRight className="w-5 h-5 text-gray-400" />
      )}
    </div>
    <div>
      <p className="text-sm text-gray-600">{title}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
    </div>
  </div>
)

const QuickActionButton: React.FC<{
  label: string
  icon: any
  onClick: () => void
}> = ({ label, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
  >
    <Icon className="w-6 h-6 text-gray-600 mb-2" />
    <span className="text-sm text-gray-700">{label}</span>
  </button>
)

const HealthIndicator: React.FC<{
  label: string
  status: 'healthy' | 'warning' | 'error'
}> = ({ label, status }) => {
  const statusColors = {
    healthy: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500'
  }

  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-700">{label}</span>
      <div className="flex items-center space-x-2">
        <span className={`text-xs uppercase ${
          status === 'healthy' ? 'text-green-600' : 
          status === 'warning' ? 'text-yellow-600' : 
          'text-red-600'
        }`}>
          {status}
        </span>
        <div className={`w-3 h-3 rounded-full ${statusColors[status]}`}></div>
      </div>
    </div>
  )
}

export default AdminDashboard